// Deps
import { FelaComponent, createComponent } from "react-fela";
import { textStyles } from "~/styleguide/typography";
import { combineRules } from "fela";
import { gutters } from "~/styleguide";
import { fluid } from "~/styleguide/utils";
import dynamic from "next/dynamic";

// Components
import Visual from "~/components/shared/visual";
import { ContentMaxWidth } from "~/components/layout/max-width";
import WordpressLink, {
    getLinkFromRelationship
} from "~/components/global/links";

const Animation = dynamic(() => import("~/components/shared/animation"), {
    ssr: false
});

export default function ValueGrid({ block }) {
    const { heading, values, anchor_id, alignment, columns } = block;

    const theValue = (value, collectionLength, index) => {
        const hasAnimation = !!value.animation_file;

        const { link_content, link_type, cta_link } = value;

        const link = getLinkFromRelationship(link_type, link_content, cta_link);

        return (
            <ValueContent
                collectionLength={collectionLength}
                key={index}
                alignment={alignment}
                columns={columns}
            >
                {(value.image || hasAnimation) && (
                    <IconContainer hasAnimation={hasAnimation}>
                        {!hasAnimation && value.image && (
                            <Visual
                                extend={imageStyles(alignment)}
                                image={value.image}
                                background="contain"
                            />
                        )}

                        {hasAnimation && (
                            <Animation
                                data={value.animation_file}
                                playOnHover={true}
                            />
                        )}
                    </IconContainer>
                )}

                {value.heading &&
                    <FelaComponent
                        as="h4"
                        style={combineRules(textStyles.heading4, valueHeading)}
                    >
                        <span dangerouslySetInnerHTML={{ __html: value.heading }} />
                    </FelaComponent>
                }

                <ValueDescription
                    extend={textStyles.paragraphSmall}
                    dangerouslySetInnerHTML={{
                        __html: value.description
                    }}
                />

                {link && value && link.url && value.cta_label && (
                    <WordpressLink
                        label={value.cta_label}
                        url={link.url}
                        slug={link.slug}
                        layout="text"
                        extend={{
                            fontSize: 20,
                            display: "inline-block",
                            extend: [fluid("margin-top", { max: 30, min: 20 })]
                        }}
                    />
                )}
            </ValueContent>
        );
    };

    return (
        <Container id={anchor_id} className="module-value-grid">
            <ContentMaxWidth>
                {/* Heading */}
                {heading && (
                    <FelaComponent
                        as="h3"
                        style={combineRules(
                            textStyles.heading3,
                            componentHeading
                        )}
                    >
                        <span dangerouslySetInnerHTML={{ __html: heading }} />
                    </FelaComponent>
                )}

                {values && values.length && (
                    <ValueGridInner alignment={alignment}>
                        {values.map((value, index) => {
                            return theValue(value, values.length, index);
                        })}
                    </ValueGridInner>
                )}
            </ContentMaxWidth>
        </Container>
    );
}

const Container = createComponent(
    () => ({
        overflow: "hidden",

        extend: [
            fluid("paddingH", gutters.default),
            fluid("paddingV", gutters.vertical)
        ]
    }),
    "div",
    ["id"]
);

const IconContainer = createComponent(({ hasAnimation, alignment }) => ({
    overflow: "hidden",
    position: "relative",
    marginBottom: 30,

    extend: [
        fluid("margin-bottom", { max: 30, min: 20 }),
        {
            condition: hasAnimation,
            style: {
                extend: [fluid("height", { max: 72, min: 50 })]
            }
        },
        {
            condition: alignment === "center",
            style: {
                marginH: "auto"
            }
        }
    ]
}));

const componentHeading = {
    textAlign: "center",
    maxWidth: 800,
    marginLeft: "auto",
    marginRight: "auto",
    extend: [fluid("marginBottom", { min: 40, max: 80 })],

    maxWidthTablet: {
        maxWidth: 500,
        marginH: "auto",
        paddingH: 20
    }
};

const valueHeading = {
    fontWeight: "bold",
    marginBottom: 20,
    "& span a": {
        color: "inherit"
    }
};

const ValueGridInner = createComponent(({ alignment }) => ({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "left",

    extend: [
        {
            condition: alignment === "center",
            style: {
                justifyContent: "center"
            }
        }
    ]
}));

const imageStyles = alignment => ({
    "& .vv-visual": {
        width: "100%",
        height: "100%"
    },

    width: 72,
    height: 72,

    maxWidthMobile: {
        marginH: "auto",
        width: 54,
        height: 54
    },

    extend: [
        {
            condition: alignment === "center",
            style: {
                marginH: "auto"
            }
        }
    ]
});

const ValueContent = createComponent(
    ({ collectionLength, alignment, columns }) => ({
        minWidthTabletLandscape: {
            grid: {
                cols: "3",
                margins: 40,
                bottomMargin: 60,
                length: collectionLength
            },
            extend: [
                {
                    condition: columns === "two",
                    style: {
                        grid: {
                            cols: "2",
                            margins: 20,
                            bottomMargin: 60,
                            length: collectionLength
                        }
                    }
                },
                {
                    condition: columns === "four",
                    style: {
                        grid: {
                            cols: "4",
                            margins: 60,
                            bottomMargin: 60,
                            length: collectionLength
                        }
                    }
                }
            ]
        },
        minWidthMobile: {
            maxWidthTabletLandscape: {
                grid: {
                    cols: "2",
                    margins: 40,
                    bottomMargin: 100 * 0.66,
                    length: collectionLength
                }
            }
        },
        maxWidthMobile: {
            marginBottom: 40,
            ":last-of-type": {
                marginBottom: 0
            },
            minWidth: "90%",
            maxWidth: "90%",
            marginH: "auto",

            // All columns are centered on mobile
            textAlign: "center"
        },
        extend: [
            {
                condition: alignment === "center",
                style: {
                    textAlign: "center"
                }
            }
        ]
    })
);

const ValueDescription = createComponent(() => ({}), "p", [
    "dangerouslySetInnerHTML"
]);
